import { useRef, type CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { useInView } from 'motion/react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { HeroReveal, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { ImpactRow, UiFragment } from '@/components/landing/service'
import { HighlightHeading, PlatformCtaBand, PlatformFaq, TrialButton, hairline, ink, muted, type LayoutProps } from './shared'

/** Sample audit trail for the table fragment: roles, not people. */
const AUDIT_ROWS = [
  { time: '09:14:02', who: 'Legal ops', action: 'Signed in', target: 'Single sign-on', result: 'Allowed' },
  { time: '09:16:47', who: 'Paralegal', action: 'Generated', target: 'Engagement letter v3.docx', result: 'Allowed' },
  { time: '09:21:10', who: 'Partner', action: 'Approved', target: 'Engagement letter v3.docx', result: 'Allowed' },
  { time: '09:24:38', who: 'Contractor', action: 'Opened', target: 'Fee schedule.xlsx', result: 'Denied' },
  { time: '09:31:55', who: 'Admin', action: 'Changed role', target: 'Contractor → Viewer', result: 'Allowed' },
  { time: '09:40:19', who: 'Sales lead', action: 'Downloaded', target: 'Master services agreement.pdf', result: 'Allowed' },
]

/** Full-width audit-log table, drawn as product UI (decorative). */
function AuditTable() {
  return (
    <div aria-hidden data-ui className="overflow-hidden bg-white shadow-[0_40px_80px_-40px_rgba(10,10,10,0.35)] dark:bg-[#1c1c26]">
      <div className={cn('flex items-center justify-between gap-4 border-b px-5 py-4', hairline)}>
        <span className={cn('font-semibold', ink)}>Audit log</span>
        <span className={cn('text-sm', muted)}>Today · all users · all actions</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-left text-sm">
          <thead className={muted}>
            <tr className={cn('border-b', hairline)}>
              {['Time', 'Who', 'Action', 'Document or setting', 'Result'].map((h) => (
                <th key={h} className="px-5 py-3 font-medium">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={ink}>
            {AUDIT_ROWS.map((row, i) => (
              <tr key={row.time} className={cn('border-b last:border-b-0', hairline, i % 2 === 1 && 'bg-[var(--color-landing-gray-50)] dark:bg-white/[0.03]')}>
                <td className={cn('px-5 py-3 tabular-nums', muted)}>{row.time}</td>
                <td className="px-5 py-3 font-medium">{row.who}</td>
                <td className="px-5 py-3">{row.action}</td>
                <td className="px-5 py-3">{row.target}</td>
                <td className="px-5 py-3">
                  <span
                    className={cn(
                      'inline-block px-2 py-0.5 text-xs font-bold uppercase',
                      row.result === 'Denied' ? 'bg-[#ff00f2] text-[#0a0a0a]' : 'bg-[#00d722] text-[#0a0a0a]'
                    )}
                  >
                    {row.result}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/** One use case: its product fragment, which starts when it scrolls in. */
function UseCaseColumn({ useCase }: { useCase: LayoutProps['feature']['useCases'][number] }) {
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, amount: 0.4 })
  return (
    <StaggerItem>
      <div ref={ref} className="aspect-square">
        <UiFragment kind={useCase.fragment} title={useCase.title} industry={useCase.industry} active={inView} />
      </div>
      <span className="mt-6 inline-block bg-[var(--color-landing-primary)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
        {useCase.industry}
      </span>
      <h3 className={cn('mt-4 font-[family-name:var(--font-landing-subdisplay)] text-2xl font-bold uppercase tracking-[-0.01em]', ink)}>
        {useCase.title}
      </h3>
      <p className={cn('landing-text-body mt-2 max-w-[40ch]', muted)}>{useCase.description}</p>
    </StaggerItem>
  )
}

/**
 * Security ("numbers"): a black band where the figures are the hero, the H1
 * beneath them; a full-width audit-log table; capabilities as large hover
 * rows; the three use cases side by side with their fragments; impact;
 * FAQ; blue CTA.
 */
export function SecurityLayout({ feature }: LayoutProps) {
  const len = Math.max(3, ...feature.heroStats.map((s) => s.value.length))
  return (
    <>
      {/* 1. Numbers hero */}
      <section aria-labelledby="security-title" className="relative overflow-hidden bg-[#0a0a0a] text-white">
        <div className="landing-container pb-16 pt-16 md:pb-24 md:pt-24">
          <HeroReveal delay={0}>
            <p className="landing-text-display-lg">
              <span className="inline-block -rotate-1 bg-[var(--color-landing-accent)] px-[0.3em] text-white">
                {feature.shortLabel}.
              </span>
            </p>
          </HeroReveal>
          <HeroReveal delay={0.1}>
            <dl
              className="mt-12 grid grid-cols-1 gap-y-10 border-t border-white/20 pt-10 sm:grid-cols-3 sm:gap-x-8 md:mt-16"
              style={{ '--len': len } as CSSProperties}
            >
              {feature.heroStats.map((stat) => (
                <div key={stat.label} className="flex min-w-0 flex-col-reverse justify-end">
                  <dt className="mt-3 text-base text-white/65 md:text-lg">{stat.label}</dt>
                  <dd className="whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[clamp(2.5rem,calc(80vw/var(--len)),6rem)] uppercase leading-[1.25] tracking-[-0.03em] sm:text-[clamp(2rem,calc(26vw/var(--len)),9rem)]">
                    {stat.value}
                  </dd>
                </div>
              ))}
            </dl>
          </HeroReveal>
          <HeroReveal delay={0.2}>
            <h1 id="security-title" className="landing-text-hero-sm mt-16 text-white md:mt-24">
              <span className="block">{feature.heroLines[0]}</span>{' '}
              <span className="block text-[#8a84ff]">{feature.heroLines[1]}</span>
            </h1>
          </HeroReveal>
          <HeroReveal delay={0.3}>
            <div className="mt-10 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <p className="landing-text-body-lg max-w-[58ch] text-pretty border-l border-white/25 pl-5 text-white/70 md:text-xl">
                {feature.subheadline}
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <TrialButton surface="dark" />
                <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-cta--on-color landing-btn-sweep">
                  Book a demo
                </Link>
              </div>
            </div>
          </HeroReveal>
        </div>
      </section>

      {/* 2. The audit trail, full width */}
      <section aria-labelledby="security-value-title" className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <HighlightHeading id="security-value-title" text={feature.valueProposition.title} color="blue" />
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
          </div>
          <ScrollReveal className="mt-14 md:mt-20" amount={0.15}>
            <AuditTable />
          </ScrollReveal>
        </div>
      </section>

      {/* 3. Capabilities as large hover rows */}
      <section aria-labelledby="security-capabilities-title" className="landing-section">
        <div className="landing-container">
          <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
            <span id="security-capabilities-title">
              Built <span className="landing-highlight landing-highlight--secondary">in.</span>
            </span>
          </TextReveal>
          <StaggerContainer amount={0.1} className={cn('mt-12 border-t md:mt-16', hairline)}>
            {feature.capabilities.map((cap, i) => {
              const Icon = cap.icon
              return (
                <StaggerItem key={cap.title} className={cn('group relative overflow-hidden border-b', hairline)}>
                  <span
                    aria-hidden
                    className="absolute inset-0 bg-[var(--color-landing-accent)] transition-[clip-path] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [clip-path:inset(0_0_100%_0)] group-hover:[clip-path:inset(0_0_0_0)]"
                  />
                  <div className="relative grid items-center gap-4 py-8 md:grid-cols-[6rem_minmax(0,1fr)_minmax(0,1fr)_3rem] md:gap-8 md:py-10">
                    <span aria-hidden className="font-[family-name:var(--font-landing-display)] text-[3rem] leading-none text-[var(--color-landing-gray-300)] transition-colors duration-300 group-hover:text-white/40 dark:text-[var(--color-landing-gray-700)] md:text-[4rem]">
                      {i + 1}
                    </span>
                    <h3 className={cn('font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] tracking-[-0.01em] transition-colors duration-300 group-hover:text-white md:text-2xl', ink)}>
                      {cap.title}
                    </h3>
                    <p className={cn('landing-text-body max-w-[48ch] transition-colors duration-300 group-hover:text-white/90', muted)}>
                      {cap.description}
                    </p>
                    <Icon
                      aria-hidden
                      className="hidden h-8 w-8 -translate-x-2 text-white opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-x-0 group-hover:opacity-100 md:block"
                      strokeWidth={1.5}
                    />
                  </div>
                </StaggerItem>
              )
            })}
          </StaggerContainer>
        </div>
      </section>

      {/* 4. Use cases side by side */}
      <section aria-labelledby="security-usecases-title" className="landing-section bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <TextReveal as="h2" className={cn('landing-text-display-xl', ink)}>
            <span id="security-usecases-title">How teams use {feature.shortLabel}</span>
          </TextReveal>
          <StaggerContainer amount={0.1} className="mt-12 grid gap-14 md:mt-16 md:grid-cols-3 md:gap-8">
            {feature.useCases.map((useCase) => (
              <UseCaseColumn key={useCase.title} useCase={useCase} />
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* 5. Impact */}
      <section aria-labelledby="security-impact-title" className="landing-section">
        <div className="landing-container">
          <h2 id="security-impact-title" className={cn('mb-10 font-[family-name:var(--font-landing-display)] text-lg uppercase leading-[1.4]', ink)}>
            Business impact
          </h2>
          <ImpactRow benefits={feature.benefits} />
          <Link
            to="/contact"
            className="group mt-14 inline-flex items-center gap-2 font-semibold text-[var(--color-landing-accent)] dark:text-[#8a84ff]"
          >
            Talk to us about your security review
            <ArrowRight aria-hidden className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="blue" />
    </>
  )
}
